/**
 * @fileoverview Company name row component for the companies table
 * @active CONFIRMED - This component is actively used in the application
 *
 * This component renders the first two columns of the companies table:
 * 1. Checkbox column (index 0) - For selecting companies
 * 2. Company name column (index 1) - Displays company logo, name and external link
 *
 * The columns are configured to stay fixed/frozen when scrolling horizontally.
 * The checkbox column has a fixed width while the company name column is resizable.
 *
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/companyInfoTable/CompaniesInfoTableRow.tsx} - Parent row component
 * @see {@link src/components/companies/companiesList/newCompaniesList/types.ts} - Table context type definitions
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/shared/TdWrapper.tsx} - Table cell wrapper component
 * @see {@link src/components/companies/companiesList/newCompaniesList/data/lists.tsx} - Column definitions
 * @see {@link src/components/companies/companiesList/companiesListComponents/tableMain/TableElements.jsx} - Legacy implementation (deprecated)
 */

// src/components/companies/companiesList/newCompaniesList/companiesTable/companyInfoTable/CompanyNameRow.tsx

"use client";

import React, { useContext } from "react";

import {
  Checkbox,
  Flex,
  Icon,
  Link as ChakraLink,
  Td,
  Text,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";

import OutLinkSvg from "@/assets/svg/companiesList/out-link.svg";
import { type TableContextType } from "@/components/companies/companiesList/newCompaniesList/types";
import DefaultImage from "@/components/ui/DefaultImage";
import { useActions } from "@/hooks/useActions";
import useGetCurrentView from "@/hooks/useGetCurrentView";
import newCompaniesListSelectors from "@/store/newCompaniesList/newCompaniesList.selectors";

import TableContext from "../context/TableContext";

interface CompanyItem {
  id: string;
  company: {
    companyName: string;
    fileLogo?: string;
    slug: string;
  };
  links?: {
    urlWebsite?: string;
  };
}

interface CompanyNameRowProps {
  item: CompanyItem;
  isSelected: boolean;
}

/**
 * CompanyNameRow component renders the first sticky column of the companies table
 * It contains the company checkbox, logo, name, and external link
 *
 * @param props - Component props
 * @param props.item - Company data to display
 * @param props.isSelected - Whether the company is selected
 * @returns React component
 */
const CompanyNameRow = ({ item, isSelected }: CompanyNameRowProps) => {
  const { setSelectedCompanies, clearCompanyForCompare } = useActions();
  const selectedCompanies = useSelector(
    newCompaniesListSelectors.getSelectedCompanies()
  );
  const columns = useSelector(newCompaniesListSelectors.getColumnsFields());
  const { currentParams } = useGetCurrentView();

  const { columnsWidth, selectedRows, setSelectedRows } =
    useContext<TableContextType>(TableContext);

  /**
   * Handles company selection/deselection when checkbox is clicked
   * Updates both local context state and Redux store
   *
   * @param company - The company item being selected or deselected
   */
  const selectCompanyHandler = (company: CompanyItem) => {
    console.log("Selecting company:", company.id);
    console.log("Current selectedRows:", selectedRows);

    // Update the local context state directly for immediate UI feedback
    const isAlreadySelected = selectedRows.some(
      (row: CompanyItem) => row.id === company.id
    );
    if (isAlreadySelected) {
      // Remove from selection
      setSelectedRows((prev: CompanyItem[]) =>
        prev.filter((row: CompanyItem) => row.id !== company.id)
      );
    } else {
      // Add to selection
      setSelectedRows((prev: CompanyItem[]) => [...prev, company]);
    }

    // Also update the Redux state
    // NOTE: CompanyItem is being passed to setSelectedCompanies which expects Company type
    // This is the established pattern in the codebase, and the function handles the conversion internally
    setSelectedCompanies(company as any); // Type assertion used to match existing pattern
  };

  /**
   * Generates a valid URL for the company
   * Uses website URL if available, or falls back to Google search
   *
   * @returns Properly formatted company URL with https:// prefix
   */
  const companyUrl = () => {
    const url =
      item?.links?.urlWebsite ||
      `https://www.google.com/search?q=${item?.company?.companyName}`;
    return url?.startsWith("http://") || url?.startsWith("https://")
      ? url
      : "https://" + url;
  };

  // Enhanced debugging for the company column issue
  console.log("CompanyNameRow item:", item);
  console.log("CompanyNameRow links:", item?.links);
  console.log(
    "CompanyNameRow - Applying sticky positioning with zIndex: docked"
  );

  return (
    <Td
      px={"10px"}
      py={"4px"}
      display={"flex"}
      h={"32px"}
      pos={"sticky"}
      left={"0"}
      zIndex={"20"} // Increased z-index to ensure it's above other content
      data-company-column="true"
      bg={
        currentParams?.orderBy?.id === "company"
          ? "lightYellow"
          : isSelected
            ? "lightBlue1"
            : "white"
      }
      _groupHover={{ bg: "lightBlue2" }}
      // Replace border with box-shadow for a more solid edge
      borderRightWidth={"0px"}
      boxShadow={"2px 0 4px -2px rgba(0,0,0,0.15)"}
      borderRight={
        currentParams?.orderBy?.id === "company"
          ? "4px solid var(--chakra-colors-lightYellow)"
          : isSelected
            ? "4px solid var(--chakra-colors-lightBlue1)"
            : "4px solid white"
      }
      // Important styling to ensure sticky behavior works
      sx={{
        position: "sticky !important",
        left: "0 !important",
        zIndex: "20 !important",
        backgroundColor:
          currentParams?.orderBy?.id === "company"
            ? "var(--chakra-colors-lightYellow) !important"
            : isSelected
              ? "var(--chakra-colors-lightBlue1) !important"
              : "white !important",
      }}
      data-testid="company-name-cell"
    >
      <Flex
        align={"center"}
        columnGap={"12px"}
        pe={"10px"}
        minW={"60px"}
        w={columnsWidth && columnsWidth[0] ? `${columnsWidth[0]}px` : "auto"}
        overflow={"hidden"}
        bg={
          currentParams?.orderBy?.id === "company"
            ? "lightYellow"
            : isSelected
              ? "lightBlue1"
              : "white"
        }
        _groupHover={{ bg: "lightBlue2" }}
      >
        <Checkbox
          variant={"table"}
          value={item?.id}
          isChecked={selectedRows.some((row) => row.id === item?.id)}
          onChange={() => selectCompanyHandler(item)}
        />

        <Flex
          as={"a"}
          href={`/companies/${item?.company?.slug}`}
          onClick={() => clearCompanyForCompare()}
          align={"center"}
          justify={"center"}
          minW={"24px"}
          h={"24px"}
          borderRadius={"4px"}
          overflow={"hidden"}
          bg={
            currentParams?.orderBy?.id === "company"
              ? "lightYellow"
              : isSelected
                ? "lightBlue1"
                : "white"
          }
          _groupHover={{ bg: "lightBlue2" }}
        >
          <DefaultImage
            src={item?.company?.fileLogo || null} // Convert undefined to null for type safety
            options={{
              objectFit: "contain",
              w: "24px",
              alt: item?.company?.companyName || "Company logo",
            }}
          />
        </Flex>
        <Flex
          w={"100%"}
          align={"center"}
          justifyContent={"space-between"}
          bg={
            currentParams?.orderBy?.id === "company"
              ? "lightYellow"
              : isSelected
                ? "lightBlue1"
                : "white"
          }
          _groupHover={{ bg: "lightBlue2" }}
        >
          <Text
            as={"a"}
            href={`/companies/${item?.company?.slug}`}
            fontSize={"14px"}
            fontWeight={"700"}
            lineHeight={"1.3"}
            letterSpacing={"2%"}
            textTransform={"capitalize"}
            mb={"-4px"}
            maxW={
              columnsWidth && columnsWidth[0]
                ? `calc(${columnsWidth[0]}px - 80px)`
                : "auto"
            }
            isTruncated
            display="block" // Ensure the text is always displayed
            visibility="visible" // Ensure the text is always visible
            bg="inherit" // Inherit background color from parent
          >
            {item?.company?.companyName || "Unnamed Company"}{" "}
            {/* Provide a fallback name */}
          </Text>
          <ChakraLink
            href={companyUrl()}
            target="_blank"
            rel="noopener noreferrer nofollow"
            visibility={"hidden"}
            _groupHover={{ visibility: "visible" }}
            bg="inherit" // Inherit background color from parent
          >
            <Icon as={OutLinkSvg} />
          </ChakraLink>
        </Flex>
      </Flex>
    </Td>
  );
};

export default CompanyNameRow;
